import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('redex_admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Types
export interface GenerationJob {
  id: string;
  userId: string;
  cities: string[];
  industry: string;
  targetCount: number;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: number;
  leadsGenerated: number;
  emailsFound: number;
  startedAt: string;
  completedAt?: string;
  error?: string;
}

export interface REV108Lead {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  source: string;
  status: string;
  service_interest: string;
  notes: string;
  city: string;
  state: string;
  created_at: string;
  parsedNotes?: {
    website?: string;
    address?: string;
    title?: string;
    employeeCount?: string;
    leadScore?: number;
    emailSource?: string;
    emailConfidence?: string;
    googleVerified?: boolean;
    googleRating?: number;
  };
}

export interface GenerationRequest {
  cities: string[];
  industry: string;
  targetCount: number;
  emailRate?: number;
  verificationRate?: number;
  locationState?: string;
}

export interface REV108Stats {
  totalLeads: number;
  leadsWithEmail: number;
  emailRate: string | number;
  recentLeads: number;
  leadsByCity: Array<{ city: string; count: number }>;
}

export interface LeadsResponse {
  leads: REV108Lead[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface LeadsFilters {
  page?: number;
  limit?: number;
  search?: string;
  city?: string;
  hasEmail?: boolean;
  minScore?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Location/Hub Types
export interface StateInfo {
  name: string;
  abbrev: string;
  region: string;
}

export interface RegionInfo {
  name: string;
  states: StateInfo[];
  cityCount: number;
}

export interface LocationStats {
  totalStates: number;
  totalCities: number;
  federalHubs: number;
  defenseHubs: number;
  techHubs: number;
  citiesByRegion: Array<{ region: string; count: number }>;
}

export interface IndustryCategory {
  category: string;
  industries: string[];
}

export interface CityWithState {
  city: string;
  state: string;
  stateAbbrev: string;
  region: string;
  isFederalHub?: boolean;
  isDefenseHub?: boolean;
  isTechHub?: boolean;
}

// Service methods
export const rev108Service = {
  // Get available industries
  async getIndustries(): Promise<string[]> {
    const response = await api.get('/rev108/industries');
    return response.data.data;
  },

  // Get industries grouped by category
  async getIndustriesGrouped(): Promise<IndustryCategory[]> {
    const response = await api.get('/rev108/industries-grouped');
    return response.data.data;
  },

  // Get suggested cities with optional filters
  async getCities(filter?: 'federal' | 'defense' | 'tech' | 'top', state?: string, region?: string): Promise<string[] | CityWithState[]> {
    const params = new URLSearchParams();
    if (filter) params.append('filter', filter);
    if (state) params.append('state', state);
    if (region) params.append('region', region);
    const response = await api.get(`/rev108/cities?${params.toString()}`);
    return response.data.data;
  },

  // Get all US states
  async getStates(): Promise<StateInfo[]> {
    const response = await api.get('/rev108/states');
    return response.data.data;
  },

  // Get all regions with states
  async getRegions(): Promise<RegionInfo[]> {
    const response = await api.get('/rev108/regions');
    return response.data.data;
  },

  // Get location statistics
  async getLocationStats(): Promise<LocationStats> {
    const response = await api.get('/rev108/location-stats');
    return response.data.data;
  },

  // Start a new lead generation job
  async startGeneration(request: GenerationRequest): Promise<{ jobId: string; status: string; message: string }> {
    const response = await api.post('/rev108/generate', request);
    return response.data.data;
  },

  // Get all generation jobs for the current user
  async getJobs(): Promise<GenerationJob[]> {
    const response = await api.get('/rev108/jobs');
    return response.data.data;
  },

  // Get status of a specific job
  async getJobStatus(jobId: string): Promise<GenerationJob> {
    const response = await api.get(`/rev108/jobs/${jobId}`);
    return response.data.data;
  },

  // Get generated leads with filters
  async getLeads(filters: LeadsFilters = {}): Promise<LeadsResponse> {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.city) params.append('city', filters.city);
    if (filters.hasEmail !== undefined) params.append('hasEmail', filters.hasEmail.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await api.get(`/rev108/leads?${params.toString()}`);
    return response.data.data;
  },

  // Get lead generation statistics
  async getStats(): Promise<REV108Stats> {
    const response = await api.get('/rev108/stats');
    return response.data.data;
  },

  // Delete a lead
  async deleteLead(id: string): Promise<void> {
    await api.delete(`/rev108/leads/${id}`);
  },

  // Convert a lead to regular contact
  async convertLead(id: string): Promise<void> {
    await api.post(`/rev108/leads/${id}/convert`);
  },

  // Export leads to CSV
  async exportLeads(leadIds?: string[], filters?: LeadsFilters): Promise<Blob> {
    const response = await api.post('/rev108/leads/bulk-export', { leadIds, filters }, {
      responseType: 'blob',
    });
    return response.data;
  },
};

export default rev108Service;
